import React, {useState} from 'react'
import styled from "styled-components";
import {Button, DatePicker, Form, Input, Select, Table, Tag} from "antd";
import {ColumnsType} from "antd/es/table";
import {useQuery} from "react-query";
import {useNavigate} from "react-router-dom";
import dayjs, {Dayjs} from "dayjs";
import {REPO} from "../../Repository/repo";
import {SearchRow, SearchWrap} from "../../Components";
import {queryKeys} from "../../Constant/QueryKeys";
import {DEFAULT_LIST_COUNT} from "../../Constant/Common";
import {VacationApprovalRow} from "../../Model/VacationModel";

const Wrap = styled.div`
  .filters { margin-bottom: 12px; }
`

const STATUS_COLOR: Record<string, string> = {
    "요청중": "blue",
    "승인됨": "green",
    "반려됨": "red",
    "취소됨": "default",
}

const ApprovalList: React.FC = () => {
    const navigate = useNavigate()
    const [form] = Form.useForm()
    const [my, setMy] = useState<string>("")
    const [status, setStatus] = useState<string>("")
    const [keyword, setKeyword] = useState<string>("")
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
    const [limit, setLimit] = useState<number>(DEFAULT_LIST_COUNT)
    const [offset, setOffset] = useState<number>(0)

    const {data} = useQuery(
        [...queryKeys.vacationApproval, my, status, keyword, range?.[0]?.format("YYYY-MM-DD"), range?.[1]?.format("YYYY-MM-DD"), limit, offset],
        async () => {
            const res = await REPO.VacationApproval.List({
                my: my || undefined,
                approval_status: status || undefined,
                search_query: keyword || undefined,
                date_begin: range?.[0]?.format("YYYY-MM-DD"),
                date_end: range?.[1]?.format("YYYY-MM-DD"),
                limit,
                offset,
            })
            return res.data
        }
    )

    const columns: ColumnsType<VacationApprovalRow> = [
        {title: '번호', dataIndex: 'seq', key: 'seq', width: 80},
        {title: '신청자', dataIndex: 'employee_name', key: 'employee_name', width: 120},
        {
            title: '제목',
            dataIndex: 'request_subject',
            key: 'request_subject',
            render: (v, r) => (
                <a onClick={() => navigate(`/Vacation/Approval/${r.seq}`)}>{v}</a>
            ),
        },
        {title: '휴가종류', dataIndex: 'vacation_type', key: 'vacation_type', width: 100},
        {
            title: '기간',
            key: 'period',
            width: 200,
            render: (_, r) => r.vacation_date_start === r.vacation_date_end
                ? r.vacation_date_start
                : `${r.vacation_date_start} ~ ${r.vacation_date_end}`,
        },
        {title: '일수', dataIndex: 'vacation_used_days', key: 'vacation_used_days', width: 80},
        {
            title: '신청일',
            dataIndex: 'request_date',
            key: 'request_date',
            width: 130,
            render: (v) => v?.slice(0, 10),
        },
        {
            title: '승인',
            dataIndex: 'approval_status',
            key: 'approval_status',
            render: (_, r) => {
                const persons = (r.persons || []).map(p =>
                    p.person_type.replace("결재자", "") + " " + p.employee_name + ": " + (p.person_action ?? '없음')
                )
                return (
                    <>
                        <Tag color={STATUS_COLOR[r.approval_status] || "default"}>{r.approval_status}</Tag>
                        <br/>
                        <small>{persons.map((row, i) => <span key={i}>{row}<br/></span>)}</small>
                    </>
                )
            }
        },
    ]

    const onSearch = (values: any) => {
        setMy(values.my || "")
        setStatus(values.status || "")
        setKeyword(values.search_query || "")
        setRange(values.range || null)
        setOffset(0)
    }

    return (
        <Wrap>
            <SearchWrap form={form} onFinish={onSearch}>
                <SearchRow>
                    <Form.Item label={"구분"} name={"my"}>
                        <Select style={{width: 130}} allowClear>
                            <Select.Option value={""}>전체</Select.Option>
                            <Select.Option value={"drafter"}>내가 신청</Select.Option>
                            <Select.Option value={"approval"}>내가 결재</Select.Option>
                            <Select.Option value={"referer"}>내가 참조</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label={"상태"} name={"status"}>
                        <Select style={{width: 110}} allowClear>
                            <Select.Option value={""}>전체</Select.Option>
                            <Select.Option value={"요청중"}>요청중</Select.Option>
                            <Select.Option value={"승인됨"}>승인됨</Select.Option>
                            <Select.Option value={"반려됨"}>반려됨</Select.Option>
                            <Select.Option value={"취소됨"}>취소됨</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label={"기간"} name={"range"}>
                        <DatePicker.RangePicker/>
                    </Form.Item>
                    <Form.Item label={"검색"} name={"search_query"}>
                        <Input allowClear placeholder={"신청자 또는 제목"}/>
                    </Form.Item>
                    <Button type={"primary"} htmlType={"submit"}>검색</Button>
                </SearchRow>
            </SearchWrap>

            <div style={{display: "flex", justifyContent: "flex-end", marginBottom: 8}}>
                <Button type={"primary"} onClick={() => navigate("/Vacation/Request/Create")}>휴가신청서 작성</Button>
            </div>

            <Table
                rowKey={"seq"}
                dataSource={data?.list || []}
                columns={columns}
                pagination={{
                    pageSize: limit,
                    total: data?.total || 0,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setOffset((page - 1) * (pageSize || limit))
                        if (pageSize) setLimit(pageSize)
                    },
                }}
            />
        </Wrap>
    )
}

export default ApprovalList
